import { QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, isTRPCClientError } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { AppRouter } from 'api/router';
import superjson from 'superjson';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        // Помилки доступу й валідації не повторюємо.
        if (
          isTRPCClientError<AppRouter>(error) &&
          error.data?.httpStatus &&
          error.data.httpStatus < 500
        ) {
          return false;
        }
        return count < 2;
      },
    },
  },
});

/** Ідентифікатор вкладки: сервер мітить ним події, і ця вкладка свої події пропускає. */
export const TAB_ID = crypto.randomUUID();

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/api/trpc',
      transformer: superjson,
      headers: { 'x-tab-id': TAB_ID },
      fetch: (url, options) => fetch(url, { ...options, credentials: 'same-origin' }),
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });

export type { AppRouter };

/** Текст помилки tRPC для показу користувачу. */
export function errorMessage(error: unknown): string {
  if (isTRPCClientError<AppRouter>(error)) {
    const zodError = (
      error.data as { zodError?: { fieldErrors: Record<string, string[]> } } | undefined
    )?.zodError;
    const first = zodError && Object.values(zodError.fieldErrors).flat()[0];
    return first ?? error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

export function isConflictError(error: unknown): boolean {
  return isTRPCClientError<AppRouter>(error) && error.data?.code === 'CONFLICT';
}
